import React from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

/**
 * Error Boundary Component
 * Catches JavaScript errors in child components and displays fallback UI
 */
const isChunkLoadError = (error) => {
  const msg = (error && (error.message || error.toString())) || '';
  return (
    error?.name === 'ChunkLoadError' ||
    /Loading chunk [\w-]+ failed|Loading CSS chunk|ChunkLoadError|import\(\) failed|Failed to fetch dynamically imported module/i.test(msg)
  );
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      recovering: false,
    };
  }

  static getDerivedStateFromError(error) {
    // A ChunkLoadError almost always means the client is holding a stale
    // bundle that references chunk hashes which no longer exist after a
    // redeploy/recompile. Show a friendly loader while we self-heal.
    if (isChunkLoadError(error)) {
      return { hasError: true, recovering: true };
    }
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // One-time self-heal for stale-chunk errors: purge caches and hard-reload
    // so the freshest index.html + chunk hashes are fetched. Guarded by a
    // session flag to avoid reload loops.
    if (isChunkLoadError(error) && !sessionStorage.getItem('chunkReloaded')) {
      sessionStorage.setItem('chunkReloaded', '1');
      (async () => {
        try {
          if ('caches' in window) {
            const keys = await caches.keys();
            await Promise.all(keys.map((k) => caches.delete(k)));
          }
        } catch (e) { /* ignore */ }
        // Unregister any stale service worker too — otherwise a stuck PWA can
        // keep handing back the same outdated bundle on the next load.
        try {
          if ('serviceWorker' in navigator) {
            const regs = await navigator.serviceWorker.getRegistrations();
            await Promise.all(regs.map((r) => r.unregister()));
          }
        } catch (e) { /* ignore */ }
        window.location.reload();
      })();
      return;
    }

    this.setState({ error, errorInfo });

    // Send to error tracking service (Sentry, LogRocket, etc.)
    if (window.posthog) {
      window.posthog.capture('error_boundary_triggered', {
        error: error.toString(),
        errorInfo: errorInfo.componentStack,
      });
    }
  }

  handleReload = async () => {
    // Hard recovery: clear all caches and unregister any stale service worker
    // so the reload fetches the freshest app shell + chunks straight from the
    // network. This reliably unsticks an installed PWA trapped on an old build.
    try {
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch (e) { /* ignore */ }
    try {
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
    } catch (e) { /* ignore */ }
    try { sessionStorage.removeItem('chunkReloaded'); } catch (e) { /* ignore */ }
    window.location.reload();
  };

  handleGoHome = async () => {
    try {
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
    } catch (e) { /* ignore */ }
    try { sessionStorage.removeItem('chunkReloaded'); } catch (e) { /* ignore */ }
    window.location.href = '/';
  };

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.recovering) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center p-4">
          <div className="text-center">
            <RefreshCw className="w-10 h-10 text-primary animate-spin mx-auto mb-4" />
            <p className="text-slate-700 dark:text-slate-200 font-medium">Updating to the latest version…</p>
            <p className="text-sm text-slate-500 mt-1">This only takes a moment.</p>
          </div>
        </div>
      );
    }

    if (this.state.hasError) {
      const isDevelopment = process.env.NODE_ENV === 'development';

      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center p-4">
          <Card className="max-w-2xl w-full shadow-xl">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-full">
                  <AlertTriangle className="w-12 h-12 text-red-600 dark:text-red-400" />
                </div>
              </div>
              <CardTitle className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                Oops! Something went wrong
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="text-center">
                <p className="text-slate-600 dark:text-slate-400 mb-4">
                  We're sorry for the inconvenience. The application encountered an unexpected error.
                </p>
                <p className="text-sm text-slate-500 dark:text-slate-500">
                  Don't worry, your data is safe. Try refreshing the page or return to the home screen.
                </p>
              </div>

              {/* Error Details (Development Only) */}
              {isDevelopment && this.state.error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                  <p className="text-sm font-mono text-red-900 dark:text-red-300 mb-2">
                    <strong>Error:</strong> {this.state.error.toString()}
                  </p>
                  {this.state.errorInfo && (
                    <details className="mt-2">
                      <summary className="text-xs font-semibold text-red-800 dark:text-red-400 cursor-pointer">
                        Component Stack
                      </summary>
                      <pre className="text-xs mt-2 overflow-auto max-h-64 text-red-900 dark:text-red-300">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </details>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={this.handleReload}
                  className="flex items-center gap-2"
                  size="lg"
                >
                  <RefreshCw className="w-4 h-4" />
                  Reload Page
                </Button>
                <Button
                  onClick={this.handleGoHome}
                  variant="outline"
                  className="flex items-center gap-2"
                  size="lg"
                >
                  <Home className="w-4 h-4" />
                  Go to Home
                </Button>
              </div>

              {/* Contact Support */}
              <div className="text-center pt-4 border-t border-slate-200 dark:border-slate-700">
                <p className="text-xs text-slate-500 dark:text-slate-500">
                  If this problem persists, please{' '}
                  <a
                    href="mailto:support@example.com"
                    className="text-primary hover:underline font-medium"
                  >
                    contact support
                  </a>
                  {' '}with details about what you were doing when the error occurred.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
